import { useState } from "react";
import { Box, CircularProgress, Stack } from "@mui/material";
import { ReuseInput } from "../ui";
import { EMAIL_REGEX } from "../../utils/regex";
import { getErrorMessage } from "../../utils/getErrorMessage";
import { toast } from "react-toastify";
import { CustomToast } from "../ui/CustomToast";
import { useSentLoginLinkMutation } from "../../api";
import { EnvelopeIcon } from "../images";
import { CustomButton } from "../ui/CustomButton";

type Errors = { [key: string]: boolean };

const validation: { [key: string]: (value: string) => boolean } = {
  email: (value: string) => !value || !EMAIL_REGEX.test(value),
};

type Fields = {
  email: string;
};

export const LoginForm = ({
  successHandler,
}: {
  successHandler: (email: string) => void;
}) => {
  const [errorMessage, setErrorMessage] = useState<Fields>({ email: "" });
  const [formData, setFormData] = useState<Fields>({ email: "" });
  const [error, setError] = useState<Errors>({
    email: false,
  });

  const [sentLoginLink, { isLoading }] = useSentLoginLinkMutation();

  const setErrorHandler = (field: string, value: boolean) => {
    setError((prev) => ({ ...prev, [field]: value }));
  };

  const inputChangeHandler = (field: string, value: string) => {
    setErrorMessage((prev) => ({ ...prev, [field]: "" }));
    setFormData((prev) => ({ ...prev, [field]: value }));
    setError((prev) => ({
      ...prev,
      [field]: validation[field](value),
    }));
  };

  const sentAccessLinkHandler = () => {
    const email = formData.email;
    if (!email.trim())
      return setErrorMessage({ email: "Valid email is required" });

    sentLoginLink({ email })
      .unwrap()
      .then(() => {
        successHandler(email);
      })
      .catch((error) => {
        if (!getErrorMessage(error).globalError) {
          setErrorMessage((err) => ({
            ...err,
            ...getErrorMessage(error).fieldErrors,
          }));
        } else {
          toast.error(
            <CustomToast
              type={"error"}
              text={
                getErrorMessage(error).globalError ||
                "Something went wrong during the password reset. Please try again"
              }
              title={"Failed getting access link"}
            />,
          );
        }
      });
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", width: "100%" }}>
      <Box
        gap="32px"
        display="flex"
        flexDirection="column"
        paddingBottom="32px"
        paddingTop="16px"
      >
        <ReuseInput
          name="email"
          handleInputChange={inputChangeHandler}
          errorMessage={errorMessage.email || "Valid email is required"}
          value={formData.email || ""}
          label="Email"
          placeholder="example@email.com"
          isError={error.email || !!errorMessage.email}
          setErrorHandler={setErrorHandler}
        />
      </Box>
      <Stack gap="8px" alignItems="flex-start">
        <CustomButton
          onClick={sentAccessLinkHandler}
          fullWidth={true}
          variant="outlined"
          customColor="#FFFFFF"
          textColor="#1D3E70"
          type="button"
          startIcon={
            isLoading ? (
              <CircularProgress size={20} sx={{ color: "inherit" }} />
            ) : (
              <EnvelopeIcon />
            )
          }
          disabled={isLoading || !!errorMessage.email}
          sx={{ height: "48px", borderColor: "#1D3E70" }}
        >
          Send Access Link
        </CustomButton>
      </Stack>
    </Box>
  );
};
